import { Button } from '@shadcn/ui/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@shadcn/ui/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { AlarmClock, CalendarDays, X } from 'lucide-react';
import { FC } from 'react';
import { useSnoozeDialogV2 } from '../dialogs/snooze-dialog-v2';
import { cn } from '@shadcn/ui/lib/utils';
import { useMutation } from '@apollo/client';
import {
    CREATE_CONTACTS_INTERACTIONS,
    GET_CONTACTS_INTERACTIONS,
    GET_CONTACTS_WEIGHTED_SCORES,
    MARK_ACTION_AS_NOT_INTERESTED,
    UNDO_MARK_ACTION_AS_NOT_INTERESTED,
} from '@zaplify/graphql';
import { useToast } from '@shadcn/ui/hooks/use-toast';
import { getUserInteractionWeights, ContactClassificationMetadata } from '@andsend/intelligence-shared';

interface QuickSnoozeDropdownProps {
    contactFirstName: string;
    contactId: string;
    contact?: any;
    onSnooze?: (e: React.MouseEvent) => void;
    onDropdownStateChange?: (isOpen: boolean) => void;
    variant?: 'default' | 'ghost' | 'outline';
    size?: 'sm' | 'icon-sm' | 'icon';
    className?: string;
    showLabel?: boolean;
    showStopSuggestingActions?: boolean;
}

export const QuickSnoozeDropdown: FC<QuickSnoozeDropdownProps> = ({
    contactFirstName,
    contactId,
    contact,
    onSnooze,
    onDropdownStateChange,
    variant = 'ghost',
    size = 'icon-sm',
    className,
    showLabel = false,
    showStopSuggestingActions = false,
}) => {
    const { openDialogForContact, quickSnooze, SnoozeDialogV2Component } = useSnoozeDialogV2();
    const [markActionAsNotInterested] = useMutation(MARK_ACTION_AS_NOT_INTERESTED);
    const [undoMarkActionAsNotInterested] = useMutation(UNDO_MARK_ACTION_AS_NOT_INTERESTED);
    const [createContactsScoringWeights] = useMutation(CREATE_CONTACTS_INTERACTIONS);
    const { toast } = useToast();

    const handleQuickSnooze = (e: React.MouseEvent, days: number, label: string) => {
        e.preventDefault();
        e.stopPropagation();
        onSnooze?.(e);
        quickSnooze(days, label, contact);
    };

    const handleOpenDialog = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onSnooze?.(e);
        openDialogForContact(contactFirstName, contactId);
    };

    const handleStopSuggestingActions = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onSnooze?.(e);

        if (contact?.userSetAsNotInterestedAt) {
            undoMarkActionAsNotInterested({
                variables: { contactId },
                refetchQueries: [GET_CONTACTS_WEIGHTED_SCORES],
            }).then(() => {
                toast({
                    title: 'Resumed suggesting actions',
                });
            });
        } else {
            markActionAsNotInterested({
                variables: { contactId },
                refetchQueries: [GET_CONTACTS_WEIGHTED_SCORES],
            }).then(() => {
                toast({
                    title: 'Stopped suggesting actions',
                    description: `Actions for ${contactFirstName} will be paused until they reply.`,
                });
            });
        }

        if (contact?.metadata) {
            const weights = getUserInteractionWeights(contact.metadata as ContactClassificationMetadata, -0.1);
            createContactsScoringWeights({
                variables: {
                    userId: contact.userId || '',
                    organizationId: contact.organizationId || '',
                    contactId: contactId,
                    contactMetadata: contact.metadata || {},
                    weights: weights,
                    interactionType: 'NOT_INTERESTED',
                },
                refetchQueries: [GET_CONTACTS_INTERACTIONS],
            });
        }
    };

    return (
        <>
            <DropdownMenu onOpenChange={onDropdownStateChange}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant={variant}
                                size={size}
                                className={cn(
                                    'data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
                                    className
                                )}
                            >
                                <AlarmClock
                                    className={cn(
                                        'h-4 w-4 flex-shrink-0',
                                        variant === 'outline' ? 'text-current' : 'text-gray-500'
                                    )}
                                />
                                {showLabel && <span className="hidden lg:inline">Snooze</span>}
                            </Button>
                        </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Snooze...</p>
                    </TooltipContent>
                </Tooltip>
                <DropdownMenuContent
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <DropdownMenuItem onClick={(e) => handleQuickSnooze(e, 1, 'Tomorrow')}>Tomorrow</DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => handleQuickSnooze(e, 7, 'Next week')}>Next week</DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => handleQuickSnooze(e, 30, 'Next month')}>
                        Next month
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex justify-between" onClick={handleOpenDialog}>
                        Pick a date
                        <CalendarDays className="h-3 w-3" />
                    </DropdownMenuItem>
                    {showStopSuggestingActions && (
                        <>
                            <DropdownMenuSeparator />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <DropdownMenuItem onClick={handleStopSuggestingActions}>
                                        {contact?.userSetAsNotInterestedAt
                                            ? 'Resume suggesting actions'
                                            : 'Stop suggesting actions'}
                                    </DropdownMenuItem>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                    <span>Actions for this contact will not appear in your feed.</span>
                                </TooltipContent>
                            </Tooltip>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            <SnoozeDialogV2Component />
        </>
    );
};
